import { useEffect, useState } from "react";

//material ui imports
import {
  Grid,
  TextField,
  Typography,
  Divider,
  Button,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Card,
  CardHeader,
  Snackbar,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import { useTheme } from "@mui/material/styles";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ContactsIcon from "@mui/icons-material/Contacts";
import WorkIcon from "@mui/icons-material/Work";

//project imports
import config from "../../../../config";

//third party imports
import * as Yup from "yup";
import { useFormik } from "formik";
import useAxiosAuth from "../../../../hooks/useAxiosAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { convertDateToLocalReverse } from "../../../../utils/date-operations";

const GeneralInfoForm = (props) => {
  //Constants
  const { onSave, onCancel, candidate } = props;
  const theme = useTheme();
  const navigate = useNavigate();
  const axiosAuth = useAxiosAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  //States
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];
  const stateOptions = [
    { value: "Maharashtra", label: "Maharashtra" },
    { value: "Goa", label: "Goa" },
    { value: "Karnataka", label: "Karnataka" },
  ];
  const maritialOptions = [
    { value: "Married", label: "Married" },
    { value: "Unmarried", label: "Unmarried" },
  ];
  const salutationOptions = [
    { value: "Mr.", label: "Mr." },
    { value: "Mrs.", label: "Mrs." },
    { value: "Miss.", label: "Miss." },
    { value: "Dr.", label: "Dr." },
  ];
  const formik = useFormik({
    initialValues: {
      id: 0,
      salutation: "Mr.",
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      dateOfBirth: convertDateToLocalReverse(new Date()),
      phoneNumber: "",
      addressLine1: "",
      addressLine2: "",
      gender: "Male",
      city: "",
      taluka: "",
      district: "",
      state: "Maharashtra",
      marritalStatus: "Married",
      guardianName: "",
      guardianPhoneNumber: "",
      referenceName: "",
      referencePhoneNumber: "",
      jobExperience: 1,
      currentJobStatus: 2,
      languageKnown: "",
      commentBox: "",
      aadharNumber: "",
      registrationStatus: 2,
      createDate: convertDateToLocalReverse(new Date()),
      modifiedDate: convertDateToLocalReverse(new Date()),
      isDeleted: false,
      deleteDate: convertDateToLocalReverse(new Date()),
      canDelete: false,
      photo: "",
      candidateSpecializations: [],
      candidateSubSpecializations: [],
      candidatePayments: [],
      candidateFollowUps: [],
      candidateRegistrations: [],
      skills: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required"),
      middleName: Yup.string().max(50, "Must be 50 characters or less"),
      lastName: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required"),
      email: Yup.string("Enter your email")
        .max(50, "Must be 50 characters or less ")
        .email("Enter a valid email")
        .required("Email is required"),
      dateOfBirth: Yup.date("must be valid date")
        .min(new Date("01-01-1900"), "Min date allowed is 01-01-1900")
        .max(new Date(), "Future date is not allowed")
        .required("Required"),
      createDate: Yup.date("must be valid date").required("Required"),
      phoneNumber: Yup.string()
        .required("Required")
        .max(10, "Mobile no. must have 10 digits")
        .min(10, "Mobile no. must have 10 digits")
        .matches(
          /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
          "Phone number is not valid. Only numbers are allowed"
        ),
      addressLine1: Yup.string().max(255, "Must be 255 characters or less"),
      addressLine2: Yup.string().max(255, "Must be 255 characters or less"),
      district: Yup.string().max(100, "Must be 100 characters or less"),
      city: Yup.string().max(100, "Must be 100 characters or less"),
      taluka: Yup.string().max(100, "Must be 100 characters or less"),
      languageKnown: Yup.string().max(100, "Must be 100 characters or less"),
      guardianName: Yup.string().max(255, "Must be 255 characters or less"),
      referenceName: Yup.string().max(255, "Must be 255 characters or less"),
      commentBox: Yup.string().max(255, "Must be 255 characters or less"),
      aadharNumber: Yup.string()
        .min(12, "minimum 12 digits allowed")
        .max(12, "maximum 12 digits allowed")
        .required("Aadhaar number is required"),
      skills: Yup.string(),
    }),

    onSubmit: (values) => {
      setLoading(true);
      if (searchParams.get("Id") == null) {
        axiosAuth
          .post("/api/Candidates", values)
          .then((response) => {
            console.log("responseData", response);
            if (response.status === 200) {
              setLoading(false);
              setSnackbar({
                open: true,
                message: "Candidate added successfully!",
                severity: "success",
              });
              setTimeout(() => navigate(-1), 1500);
            } else {
              setLoading(false);
              setErrors({ submit: response.data });
              setSnackbar({
                open: true,
                message: response.data,
                severity: "error",
              });
            }
          })
          .catch((error) => {
            setErrors({ submit: error.response.data });
            setSnackbar({
              open: true,
              message: error.response.data,
              severity: "error",
            });
            console.log(error);
            setLoading(false);
          });
      } else {
        axiosAuth
          .put("/api/Candidates", values)
          .then((response) => {
            if (response.status === 200) {
              setLoading(false);
              setSnackbar({
                open: true,
                message: "Candidate updated successfully!",
                severity: "success",
              });
              setTimeout(() => navigate(-1), 1500);
            } else {
              setLoading(false);
              setErrors({ submit: response.data });
              setSnackbar({
                open: true,
                message: response.data,
                severity: "error",
              });
            }
          })
          .catch((error) => {
            setErrors({ submit: error.response.data });
            setSnackbar({
              open: true,
              message: error.response.data,
              severity: "error",
            });
            console.log(error);
            setLoading(false);
          });
      }
    },
  });

  //UseEffects
  useEffect(() => {
    if (searchParams.get("Id") != null) {
      let id = searchParams.get("Id");
      axiosAuth
        .get("/api/Candidates/" + id)
        .then((response) => {
          console.log(response);
          if (response.status === 200) {
            let tempData = response.data;
            if (response.data.dateOfBirth == null) {
              tempData.dateOfBirth = new Date().toISOString()?.split("T")[0];
            } else {
              tempData.dateOfBirth = new Date(
                response.data.dateOfBirth?.toString() + "Z"
              )
                ?.toISOString()
                ?.split("T")[0];
            }
            if (response.data.createDate == null) {
              tempData.createDate = new Date().toISOString()?.split("T")[0];
            } else {
              tempData.createDate = new Date(
                response.data.createDate?.toString() + "Z"
              )
                ?.toISOString()
                ?.split("T")[0];
            }
            formik.setValues(tempData);
          } else {
            console.log("Error");
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  console.log("errorssss", errors);
  return (
    <>
      <Grid maxWidth="xl" sx={{ marginX: "auto", paddingY: 4, paddingX: 2 }}>
        <Card variant="outlined" sx={{ padding: 2 }}>
          <CardHeader
            avatar={<AccountBalanceIcon color="primary" />}
            title={
              <Typography variant="h5">Candidate Registration Form</Typography>
            }
            sx={{ paddingBottom: 0 }}
          />
          <Divider sx={{ marginY: 2 }} />
          <Grid container spacing={2}>
            <Grid item xl={12} lg={12} md={12} sm={12}>
              <form onSubmit={formik.handleSubmit}>
                {/* Personal Information Section */}
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Typography variant="h6">Personal Information</Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  {/* Primary Identity Information - First row */}
                  <Grid item md={3} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Salutation</InputLabel>
                      <Select
                        name="salutation"
                        label="Salutation"
                        onChange={formik.handleChange}
                        value={formik.values.salutation}
                        error={
                          formik.touched.salutation &&
                          Boolean(formik.errors.salutation)
                        }
                        helperText={
                          formik.touched.salutation && formik.errors.salutation
                        }
                      >
                        {salutationOptions.map((options) => (
                          <MenuItem key={options.value} value={options.value}>
                            {options.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Name Fields - Second row */}
                  <Grid item md={3} xs={12}>
                    <TextField
                      name="firstName"
                      label="First Name *"
                      onChange={formik.handleChange}
                      value={formik.values.firstName}
                      fullWidth
                      error={
                        formik.touched.firstName &&
                        Boolean(formik.errors.firstName)
                      }
                      helperText={
                        formik.touched.firstName && formik.errors.firstName
                      }
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <TextField
                      name="middleName"
                      label="Middle Name"
                      onChange={formik.handleChange}
                      value={formik.values.middleName}
                      fullWidth
                      error={
                        formik.touched.middleName &&
                        Boolean(formik.errors.middleName)
                      }
                      helperText={
                        formik.touched.middleName && formik.errors.middleName
                      }
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <TextField
                      name="lastName"
                      label="Last Name *"
                      onChange={formik.handleChange}
                      value={formik.values.lastName}
                      fullWidth
                      error={
                        formik.touched.lastName &&
                        Boolean(formik.errors.lastName)
                      }
                      helperText={
                        formik.touched.lastName && formik.errors.lastName
                      }
                    />
                  </Grid>

                  {/* Personal Details - Third row */}
                  <Grid item md={4} xs={12}>
                    <TextField
                      name="dateOfBirth"
                      type="date"
                      label="Date of Birth *"
                      onChange={formik.handleChange}
                      value={formik.values.dateOfBirth}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={
                        formik.touched.dateOfBirth &&
                        Boolean(formik.errors.dateOfBirth)
                      }
                      helperText={
                        formik.touched.dateOfBirth && formik.errors.dateOfBirth
                      }
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        name="gender"
                        label="Gender"
                        onChange={formik.handleChange}
                        value={formik.values.gender}
                        error={
                          formik.touched.gender && Boolean(formik.errors.gender)
                        }
                        helperText={
                          formik.touched.gender && formik.errors.gender
                        }
                      >
                        {genderOptions.map((options) => (
                          <MenuItem key={options.value} value={options.value}>
                            {options.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Marital Status</InputLabel>
                      <Select
                        name="marritalStatus"
                        label="Marital Status"
                        onChange={formik.handleChange}
                        value={formik.values.marritalStatus}
                        error={
                          formik.touched.marritalStatus &&
                          Boolean(formik.errors.marritalStatus)
                        }
                        helperText={
                          formik.touched.marritalStatus &&
                          formik.errors.marritalStatus
                        }
                      >
                        {maritialOptions.map((options) => (
                          <MenuItem key={options.value} value={options.value}>
                            {options.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item md={4} xs={12}>
                    <TextField
                      name="createDate"
                      type="date"
                      label="Registration Date *"
                      onChange={formik.handleChange}
                      value={formik.values.createDate}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={
                        formik.touched.createDate &&
                        Boolean(formik.errors.createDate)
                      }
                      helperText={
                        formik.touched.createDate && formik.errors.createDate
                      }
                    />
                  </Grid>

                  {/* Contact fields */}
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="phoneNumber"
                      label="Mobile Number *"
                      onChange={formik.handleChange}
                      value={formik.values.phoneNumber}
                      fullWidth
                      error={
                        formik.touched.phoneNumber &&
                        Boolean(formik.errors.phoneNumber)
                      }
                      helperText={
                        formik.touched.phoneNumber && formik.errors.phoneNumber
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="email"
                      label="Email Address *"
                      onChange={formik.handleChange}
                      value={formik.values.email}
                      fullWidth
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>

                  {/* Language */}
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="aadharNumber"
                      label="Aadhaar Number *"
                      onChange={formik.handleChange}
                      value={formik.values.aadharNumber}
                      fullWidth
                      error={
                        formik.touched.aadharNumber &&
                        Boolean(formik.errors.aadharNumber)
                      }
                      helperText={
                        formik.touched.aadharNumber &&
                        formik.errors.aadharNumber
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="languageKnown"
                      label="Languages Known"
                      onChange={formik.handleChange}
                      value={formik.values.languageKnown}
                      fullWidth
                      error={
                        formik.touched.languageKnown &&
                        Boolean(formik.errors.languageKnown)
                      }
                      helperText={
                        formik.touched.languageKnown &&
                        formik.errors.languageKnown
                      }
                    />
                  </Grid>
                </Grid>

                {/* Address Section */}
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <Grid item>
                    <Typography variant="h6">Address Information</Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="addressLine1"
                      label="Address Line 1"
                      onChange={formik.handleChange}
                      value={formik.values.addressLine1}
                      fullWidth
                      error={
                        formik.touched.addressLine1 &&
                        Boolean(formik.errors.addressLine1)
                      }
                      helperText={
                        formik.touched.addressLine1 &&
                        formik.errors.addressLine1
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="addressLine2"
                      label="Address Line 2"
                      onChange={formik.handleChange}
                      value={formik.values.addressLine2}
                      fullWidth
                      error={
                        formik.touched.addressLine2 &&
                        Boolean(formik.errors.addressLine2)
                      }
                      helperText={
                        formik.touched.addressLine2 &&
                        formik.errors.addressLine2
                      }
                    />
                  </Grid>

                  <Grid item md={3} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>State</InputLabel>
                      <Select
                        name="state"
                        label="State"
                        onChange={formik.handleChange}
                        value={formik.values.state}
                        error={
                          formik.touched.state && Boolean(formik.errors.state)
                        }
                        helperText={formik.touched.state && formik.errors.state}
                      >
                        {stateOptions.map((options) => (
                          <MenuItem key={options.value} value={options.value}>
                            {options.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <TextField
                      name="district"
                      label="District"
                      onChange={formik.handleChange}
                      value={formik.values.district}
                      fullWidth
                      error={
                        formik.touched.district &&
                        Boolean(formik.errors.district)
                      }
                      helperText={
                        formik.touched.district && formik.errors.district
                      }
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <TextField
                      name="taluka"
                      label="Taluka"
                      onChange={formik.handleChange}
                      value={formik.values.taluka}
                      fullWidth
                      error={
                        formik.touched.taluka && Boolean(formik.errors.taluka)
                      }
                      helperText={formik.touched.taluka && formik.errors.taluka}
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <TextField
                      name="city"
                      label="City/Town/Village"
                      onChange={formik.handleChange}
                      value={formik.values.city}
                      fullWidth
                      error={formik.touched.city && Boolean(formik.errors.city)}
                      helperText={formik.touched.city && formik.errors.city}
                    />
                  </Grid>
                </Grid>

                {/* Guardian/Reference Section */}
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <Grid item>
                    <Typography variant="h6">
                      Guardian & Reference Information
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="guardianName"
                      label="Guardian Name"
                      onChange={formik.handleChange}
                      value={formik.values.guardianName}
                      fullWidth
                      error={
                        formik.touched.guardianName &&
                        Boolean(formik.errors.guardianName)
                      }
                      helperText={
                        formik.touched.guardianName &&
                        formik.errors.guardianName
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="guardianPhoneNumber"
                      label="Guardian Phone Number"
                      onChange={formik.handleChange}
                      value={formik.values.guardianPhoneNumber}
                      fullWidth
                      error={
                        formik.touched.guardianPhoneNumber &&
                        Boolean(formik.errors.guardianPhoneNumber)
                      }
                      helperText={
                        formik.touched.guardianPhoneNumber &&
                        formik.errors.guardianPhoneNumber
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="referenceName"
                      label="Reference Name"
                      onChange={formik.handleChange}
                      value={formik.values.referenceName}
                      fullWidth
                      error={
                        formik.touched.referenceName &&
                        Boolean(formik.errors.referenceName)
                      }
                      helperText={
                        formik.touched.referenceName &&
                        formik.errors.referenceName
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="referencePhoneNumber"
                      label="Reference Phone Number"
                      onChange={formik.handleChange}
                      value={formik.values.referencePhoneNumber}
                      fullWidth
                      error={
                        formik.touched.referencePhoneNumber &&
                        Boolean(formik.errors.referencePhoneNumber)
                      }
                      helperText={
                        formik.touched.referencePhoneNumber &&
                        formik.errors.referencePhoneNumber
                      }
                    />
                  </Grid>
                </Grid>

                {/* Professional Information Section */}
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <Grid item>
                    <Typography variant="h6">
                      Professional Information
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item md={4} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Job Experience</InputLabel>
                      <Select
                        name="jobExperience"
                        label="Job Experience"
                        onChange={formik.handleChange}
                        value={formik.values.jobExperience}
                        error={
                          formik.touched.jobExperience &&
                          Boolean(formik.errors.jobExperience)
                        }
                      >
                        <MenuItem value={1}>Fresher</MenuItem>
                        <MenuItem value={2}>Experienced</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Current Job Status</InputLabel>
                      <Select
                        name="currentJobStatus"
                        label="Current Job Status"
                        onChange={formik.handleChange}
                        value={formik.values.currentJobStatus}
                        error={
                          formik.touched.currentJobStatus &&
                          Boolean(formik.errors.currentJobStatus)
                        }
                      >
                        <MenuItem value={1}>Working</MenuItem>
                        <MenuItem value={2}>Not Working</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <TextField
                      name="skills"
                      label="Skills"
                      onChange={formik.handleChange}
                      value={formik.values.skills}
                      fullWidth
                      error={
                        formik.touched.skills && Boolean(formik.errors.skills)
                      }
                      helperText={formik.touched.skills && formik.errors.skills}
                    />
                  </Grid>
                </Grid>
                {/* {errors.submit && (
                  <Typography color="error">{errors.submit}</Typography>
                )} */}
                <Divider sx={{ my: 3 }} />

                {/* Action Buttons */}
                <Grid container spacing={2}>
                  <Grid item>
                    <LoadingButton
                      variant="contained"
                      type="submit"
                      loading={loading}
                      disabled={loading}
                      loadingPosition="end"
                      endIcon={<SaveIcon />}
                    >
                      Submit
                    </LoadingButton>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        navigate(-1);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      {/* Snackbar for showing messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            bgcolor:
              snackbar.severity === "error" ? "error.dark" : "success.dark",
            color: "common.white",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GeneralInfoForm;
